import React, { FunctionComponent } from 'react'
import { FormattedMessage } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { useLyraIntl } from '@lyrahealth-inc/shared-app-logic/src/features/intl/hooks/useLyraIntl'
import { getLegalNoticeLink } from '@lyrahealth-inc/shared-app-logic/src/features/intl/utils/getLegalNoticeLink'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { IS_LIBRARY, IS_WEB } from '../../constants'
import { getAuroraAwareBackgroundColor } from '../../styles/commonStyles'
import { ContentAreaView } from '../../templates/content/ContentArea'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { CoverageTermsContent as CoverageLink } from '../coverageTermsContent/CoverageTermsContent'

export type LegalFooterProps = {
  footerLinkPressed: (url: string) => void
  showHIPAALink?: boolean
  backgroundColor?: string
  legalNotice?: React.ReactNode
  removeBottomSpacing?: boolean
  isInternational?: boolean
}

const LinksContainer = styled(ContentAreaView)<{
  theme: ThemeType
  backgroundColor: string
  removeBottomSpacing?: boolean
}>(({ theme, backgroundColor, removeBottomSpacing }) => ({
  backgroundColor: getAuroraAwareBackgroundColor(theme.isAuroraTheme, backgroundColor),
  flexDirection: 'row',
  justifyContent: 'center',
  flexWrap: 'wrap',
  ...(!removeBottomSpacing && { bottom: '10px' }),
}))

const Divider = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  paddingHorizontal: theme.spacing['4px'],
}))

const ListItemContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
})

const shouldUseRegularFooterFontWeight = IS_WEB && !IS_LIBRARY

export const LegalFooter: FunctionComponent<LegalFooterProps> = ({
  footerLinkPressed,
  legalNotice,
  showHIPAALink = true,
  backgroundColor,
  removeBottomSpacing = false,
  isInternational = false,
}) => {
  const { activeLanguage } = useLyraIntl()
  const { colors, isAuroraTheme, isDarkMode } = useTheme()
  const textSize = BodyTextSize.SMALL
  const linkColor = isAuroraTheme && isDarkMode ? colors.linkDefault : colors.textSecondary
  const dividerText = ' | '
  const divider = (
    // eslint-disable-next-line formatjs/no-literal-string-in-jsx
    <Divider
      text={dividerText}
      size={textSize}
      color={isAuroraTheme ? colors.textSecondary : colors.dividerSecondary}
    />
  )

  // Build array of link items to render
  const linkItems: React.ReactNode[] = []

  if (legalNotice) {
    linkItems.push(
      <ListItemContainer key='coverage' role='listitem'>
        <CoverageLink fromLegalFooter legalLanguage={legalNotice} />
      </ListItemContainer>,
    )
  }

  if (showHIPAALink && !isInternational) {
    linkItems.push(
      <ListItemContainer key='hipaa' role='listitem'>
        <PressableOpacity onPress={() => footerLinkPressed(getLegalNoticeLink(activeLanguage, 'HIPAA_NOTICE'))}>
          <BodyText
            text={<FormattedMessage defaultMessage='HIPAA Notice' description='Link text for HIPAA notice' />}
            size={textSize}
            color={linkColor}
            underline
            medium={!shouldUseRegularFooterFontWeight}
            regular={shouldUseRegularFooterFontWeight}
          />
        </PressableOpacity>
      </ListItemContainer>,
    )
  }

  linkItems.push(
    <ListItemContainer key='privacy' role='listitem'>
      <PressableOpacity
        onPress={() => footerLinkPressed(getLegalNoticeLink(activeLanguage, 'PRIVACY_POLICY', isInternational))}
      >
        <BodyText
          text={<FormattedMessage defaultMessage='Privacy Policy' description='Link text for privacy policy' />}
          size={textSize}
          color={linkColor}
          underline
          medium={!shouldUseRegularFooterFontWeight}
          regular={shouldUseRegularFooterFontWeight}
        />
      </PressableOpacity>
    </ListItemContainer>,
  )

  linkItems.push(
    <ListItemContainer key='terms' role='listitem'>
      <PressableOpacity onPress={() => footerLinkPressed(getLegalNoticeLink(activeLanguage, 'TERMS_OF_USE'))}>
        <BodyText
          text={<FormattedMessage defaultMessage='Terms of Use' description='Link text for terms of use' />}
          size={textSize}
          color={linkColor}
          underline
          medium={!shouldUseRegularFooterFontWeight}
          regular={shouldUseRegularFooterFontWeight}
        />
      </PressableOpacity>
    </ListItemContainer>,
  )

  linkItems.push(
    <ListItemContainer key='accessibility' role='listitem'>
      <PressableOpacity onPress={() => footerLinkPressed(getLegalNoticeLink(activeLanguage, 'ACCESSIBILITY'))}>
        <BodyText
          text={<FormattedMessage defaultMessage='Accessibility' description='Link text for accessibility' />}
          size={textSize}
          color={linkColor}
          underline
          medium={!shouldUseRegularFooterFontWeight}
          regular={shouldUseRegularFooterFontWeight}
        />
      </PressableOpacity>
    </ListItemContainer>,
  )

  return (
    <LinksContainer
      role='list'
      backgroundColor={backgroundColor || colors.backgroundPrimary}
      removeBottomSpacing={removeBottomSpacing}
      testID={tID('LegalFooter')}
    >
      {linkItems.map((item, index) => (
        <React.Fragment key={index}>
          {item}
          {index < linkItems.length - 1 && divider}
        </React.Fragment>
      ))}
    </LinksContainer>
  )
}
